import React from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { formatMoneyPeriodHeader, getPeriodKey } from "@common/helpers/reports";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { ViewConfig } from "@lib/views";
import { buildClassColumnGrouping } from "@design-system/ViewTable/core/classColumnGrouping";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { makeClassColumnHeaderCell } from "../ClassColumnHeaderCell";
import { isEliminationsColumn } from "../consolidatedColumns";
import {
    getColumnPinOptions,
    getLabelColumnMaxSize,
} from "../singleColumnReport";
import {
    BALANCING_ROW_TOOLTIP,
    isBalancingReportRow,
    makeBalanceSheetPeriodCell,
} from "./BalanceSheetViewPeriodCell";
import { BalanceSheetViewRow } from "./balanceSheetViewTypes";
import styles from "./BalanceSheetTable.module.scss";

// ---------------------------------------------------------------------------
// Column sizing constants
// ---------------------------------------------------------------------------

const BS_LABEL_COLUMN_ID = "label";
const LABEL_MIN_SIZE = 240;
const LABEL_MAX_SIZE = 1200;
const PERIOD_MIN_SIZE = 120;
const PERIOD_MAX_SIZE = 200;

// ---------------------------------------------------------------------------
// Label cell
// ---------------------------------------------------------------------------

const renderLabelCell = React.memo(function BalanceSheetLabelCell({
    row,
}: CellContext<
    ViewTableFeatures,
    BalanceSheetViewRow,
    unknown
>): React.ReactElement {
    const { label, isTotal } = row.original;
    const labelContent = isBalancingReportRow(row.original) ? (
        <Tooltip content={BALANCING_ROW_TOOLTIP} side="bottom">
            <span>{label}</span>
        </Tooltip>
    ) : (
        label
    );
    return (
        <ViewTableCell className={isTotal ? styles.totalCell : undefined}>
            {labelContent}
        </ViewTableCell>
    );
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function partitionPeriods(periods: ReportPeriodWithLabel[]) {
    const isPinnedPeriod = (period: ReportPeriodWithLabel): boolean =>
        Boolean(period.isTotal) || isEliminationsColumn(period);
    const unpinnedPeriods = periods.filter((period) => !isPinnedPeriod(period));
    const pinnedPeriods = periods.filter(isPinnedPeriod);
    return { unpinnedPeriods, pinnedPeriods };
}

// ---------------------------------------------------------------------------
// Column record type
// ---------------------------------------------------------------------------

type BSColumnRecord = Record<
    string,
    ViewConfig<BalanceSheetViewRow, string, never>["columns"][string]
>;

// ---------------------------------------------------------------------------
// Public builders
// ---------------------------------------------------------------------------

/**
 * Builds an ephemeral ViewConfig driven by the period list.
 * Uses string as TColumn since column IDs are dynamically derived from periods.
 */
export function buildBalanceSheetConfig(
    periods: ReportPeriodWithLabel[],
    numberFormat?: NumberFormatValues,
): ViewConfig<BalanceSheetViewRow, string, never> {
    const classGrouping = buildClassColumnGrouping(periods);

    const columns: BSColumnRecord = {
        [BS_LABEL_COLUMN_ID]: {
            name: "Name",
            header: "",
            accessorKey: "label",
            cell: renderLabelCell,
            minSize: LABEL_MIN_SIZE,
            maxSize: getLabelColumnMaxSize({
                periods,
                labelMaxSize: LABEL_MAX_SIZE,
            }),
            immutable: true,
        },
    };

    periods.forEach((period, periodIndex) => {
        const isTotalPeriod = Boolean(period.isTotal);
        const columnId = isTotalPeriod
            ? `total-${getPeriodKey(period)}`
            : `period-${getPeriodKey(period)}`;

        columns[columnId] = {
            name: period.label,
            header: formatMoneyPeriodHeader(period, numberFormat),
            headerCell: makeClassColumnHeaderCell(period, numberFormat),
            accessorKey: "values",
            cell: makeBalanceSheetPeriodCell({
                period,
                periodIndex,
                allPeriods: periods,
            }),
            minSize: PERIOD_MIN_SIZE,
            maxSize: PERIOD_MAX_SIZE,
            groupPath: classGrouping?.groupPathByColumnId[columnId],
            headerTierDepth: classGrouping?.headerTierDepthByColumnId[columnId],
        };
    });

    return {
        columns,
        groups: classGrouping?.groups,
        nesting: {
            enabled: true,
            expandColumn: BS_LABEL_COLUMN_ID,
            childrenKey: "children",
        },
    };
}

/**
 * Builds an ephemeral View object (never persisted to DB).
 * Eliminations and total period columns are pinned right; no selection or
 * bulk-actions.
 */
export function buildBalanceSheetView(periods: ReportPeriodWithLabel[]): View {
    const { unpinnedPeriods, pinnedPeriods } = partitionPeriods(periods);

    return {
        type: ViewType.BALANCE_SHEET,
        columns: [
            {
                id: BS_LABEL_COLUMN_ID,
                options: getColumnPinOptions({ periods, pin: "left" }),
            },
            ...unpinnedPeriods.map((period) => ({
                id: `period-${getPeriodKey(period)}`,
            })),
            ...pinnedPeriods.map((period) => ({
                id: `${period.isTotal ? "total" : "period"}-${getPeriodKey(
                    period,
                )}`,
                options: getColumnPinOptions({ periods, pin: "right" }),
            })),
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}
